/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect, useRef, memo } from 'react';
import { useRouter } from 'next/router';
import { useQuery, useQueryClient } from 'react-query';
import { Pagination } from '@styles/globalStyles';
import { API, APIRoutes } from '@utils/api';
import { Container, Employers, Wrapper } from '@components/EmployersList/styles';

import EmployersFilters from '@components/EmployersFilers';
import routes from '@utils/routes';
import EmployerBox from '@components/EmployerBox';

type EmployersListProps = {
  employers: {
    employerProfiles: Employer[];
    meta: {
      pagination: Pagination;
    };
  };
};

type EmployerFiltersData = {
  employer: {
    fieldsOfExpertiseId?: number | null;
    name: string | null;
  };
};

async function fetchEmployers(pg = 1, expertise = null, companyName = null) {
  const { data } = await API.get(APIRoutes.employers({ page: pg, fieldsOfExpertiseId: expertise, name: companyName }));
  return data;
}

function EmployersList({ employers }: EmployersListProps) {
  const {
    meta: {
      pagination: { currentPage, pages },
    },
  } = employers || {};
  const [page, setPage] = useState(parseInt(currentPage, 10));
  const [pagesCount, setPagesCount] = useState(pages);
  const [filters, setFilters] = useState({
    expertise: null,
    name: null,
  });
  const employersRef = useRef(null);
  const isMounted = useRef(false);
  const router = useRouter();
  const queryClient = useQueryClient();

  let debounce: ReturnType<typeof setTimeout>;

  function onSubmit(query: EmployerFiltersData) {
    clearTimeout(debounce);
    debounce = setTimeout(async () => {
      const {
        employer: { fieldsOfExpertiseId, name },
      } = query;
      setFilters({ expertise: fieldsOfExpertiseId, name });
      const params = { ...(fieldsOfExpertiseId ? { fieldsOfExpertiseId } : {}), ...(name ? { name } : {}) };
      await router.replace({ pathname: routes.employer, query: params }, undefined, {
        shallow: true,
      });
      setPage(1);
    }, 500);
  }

  const { data, isFetching } = useQuery(
    ['employers', filters],
    () => fetchEmployers(page, filters.expertise, filters.name),
    {
      enabled: false,
      placeholderData: employers,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    setPagesCount(data.meta.pagination.pages);
  }, [data, filters]);

  useEffect(() => {
    if (isMounted.current) {
      queryClient.prefetchQuery(['employers', filters], () => fetchEmployers(page, filters.expertise, filters.name));
    } else {
      isMounted.current = true;
    }
  }, [filters, queryClient, page]);

  const handlePageClick = async (event) => {
    const pageNumber = event.selected + 1;
    await router.push(`?page=${pageNumber}`, undefined, { shallow: true });
    setPage(pageNumber);

    employersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    employersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  return (
    <Container ref={employersRef} id="employers">
      <EmployersFilters onSubmit={onSubmit} resetable />

      <Wrapper>
        <Employers>
          {data?.employerProfiles && data.employerProfiles.length ? (
            data.employerProfiles.map((employer: Employer) => (
              <EmployerBox key={`employer-box_${employer.id}`} employer={employer} loading={isFetching} />
            ))
          ) : (
            <p>Brak wyników</p>
          )}
        </Employers>
        <Pagination
          className="pagination"
          breakLabel="..."
          nextLabel="next"
          onPageChange={handlePageClick}
          pageRangeDisplayed={2}
          initialPage={page - 1}
          disableInitialCallback
          pageCount={pagesCount}
          previousLabel="previous"
          renderOnZeroPageCount={null}
        />
      </Wrapper>
    </Container>
  );
}

export default memo(EmployersList);
